import { useApiError } from "@raiden/library/components/ReactHookForm/ApiError";
import { CUSTOMERS_CONTACTS_CUSTOMER_TYPE_VALUE_PARTICULAR } from "@raiden/library/constants/customers/contacts";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import useRequest from "@raiden/library/hooks/useRequest";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import ConditionalWrapper from "@splitfire-agency/raiden-library/dist/components/ConditionalWrapper";
import encodeFormData from "@splitfire-agency/raiden-library/dist/libraries/utils/encodeFormData";
import { useCallback } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

/**
 * @typedef {object} ContactFormValues
 * @property {object} data
 * @property {number | ""} data.category_id
 * @property {import("@raiden/library/types/customer/contact").CustomerContactCustomerType} data.customer_type
 * @property {string} data.first_name
 * @property {string} data.last_name
 * @property {string} data.company_name
 * @property {string} data.phone_country
 * @property {string} data.phone_number
 * @property {string} data.email
 * @property {number} data.environment_id
 * @property {string} data.content
 */

/**
 * @param {object} params
 * @param {number} params.environment_id
 * @param {import("@raiden/library/types/Configuration").ConfigurationCustomersContactCategory[]} params.customersContactCategories
 * @returns {ContactFormValues}
 */
function getDefaultValues({ environment_id, customersContactCategories }) {
  const customAttributes = {};

  customersContactCategories.forEach((category) => {
    category.custom_attributes.forEach((attribute) => {
      customAttributes[attribute.alias] = "";
    });
  });

  /** @type {ContactFormValues} */
  const defaultValues = {
    data: {
      category_id: "",
      customer_type: CUSTOMERS_CONTACTS_CUSTOMER_TYPE_VALUE_PARTICULAR,
      first_name: "",
      last_name: "",
      company_name: "",
      phone_country: "FR",
      phone_number: "",
      email: "",
      environment_id,
      content: "",
    },
  };

  return defaultValues;
}

/**
 * @typedef {object} Props
 * @property {import("@raiden/library/types/Configuration").ConfigurationCustomersContactCategory[]} customersContactCategories
 * @property {() => void} [onSuccess]
 * @property {boolean} [withFormTag]
 * @property {(params: {form: import("react-hook-form").UseFormReturn<ContactFormValues>, submit: () => void, isLoading: boolean}) => import("react").ReactElement} children
 */
/**
 * @param {Props} props
 */
export default function ContactsCreateHandler({
  customersContactCategories,
  onSuccess,
  withFormTag = true,
  children,
}) {
  const intl = useIntl();

  const { bestEnvironment } = usePreferences();

  /** @type {import("react-hook-form").UseFormReturn<ContactFormValues>} */
  const form = useForm({
    defaultValues: getDefaultValues({
      environment_id: bestEnvironment?.id ?? -1,
      customersContactCategories,
    }),
    shouldUseNativeValidation: false,
  });
  const { handleSubmit } = form;

  const { request, isLoading, toastSuccess, toastError } = useRequest();

  const { resolveApiError, resetApiErrors } = useApiError();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const submit = useCallback(
    (event) => {
      event.preventDefault();
      handleSubmit(async (values) => {
        if (
          !executeRecaptcha &&
          process.env.NEXT_PUBLIC_RECAPTCHA_V3_PUBLIC_KEY
        ) {
          console.warn("Execute recaptcha not yet available");
          toastError({
            id: "invalid-recaptcha",
            status: "error",
            description: intl.formatMessage({
              defaultMessage: "Impossible de charger le module de sécurité",
            }),
            title: intl.formatMessage({
              defaultMessage: "Erreur",
            }),
            isClosable: true,
          });
          return;
        }

        const token = executeRecaptcha
          ? await executeRecaptcha("submit_form_quotation")
          : null;

        request(
          generateApiUrl({
            id: "@api.customers.contacts.create",
          }),
          {
            method: "POST",
            body: encodeFormData({
              data: values.data,
              meta: {
                notify: true,
                recaptcha_token: token,
              },
            }),
          },
          {
            inputMode: "formData",
          },
        )
          .then((response) => {
            toastSuccess({
              title: intl.formatMessage({
                defaultMessage:
                  "Les modifications ont bien été prises en compte.",
              }),
            });
            resetApiErrors();
            onSuccess?.();
          })
          .catch((response) => {
            resolveApiError({ response, form });
          });
      })();
    },
    [
      executeRecaptcha,
      form,
      handleSubmit,
      intl,
      onSuccess,
      request,
      resetApiErrors,
      resolveApiError,
      toastError,
      toastSuccess,
    ],
  );

  return (
    <FormProvider {...form}>
      <ConditionalWrapper
        hasWrapper={withFormTag}
        wrapper={(children) => <form onSubmit={submit}>{children}</form>}>
        {children({ form, submit, isLoading })}
      </ConditionalWrapper>
    </FormProvider>
  );
}
